<script lang="ts">
    import type {RecordHolding, RecordStatusTransition, User} from 'typings/portaro.be.types';
    import type {DocumentDetailView} from '../types';
    import type {UserDetailView} from 'src/features/user/types';
    import {pipe} from 'core/utils';
    import {encodeUri, loc} from 'shared/utils/pipes';
    import {exists} from 'shared/utils/custom-utils';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import {KpDocumentDetailPageService} from '../kp-document-detail-page.service';
    import KpButtonStyleAnchor from 'shared/ui-widgets/button/KpButtonStyleAnchor.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import KpActionRequestButton from 'shared/components/kp-action-request-button/KpActionRequestButton.svelte';
    import KpCollectionSingleInsertButton from '../../../record-collections/kp-record-collection-single-insert-button/KpCollectionSingleInsertButton.svelte';
    import KpDropdownMenuButton from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuButton.svelte';
    import KpDropdownMenuItem from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuItem.svelte';
    import UserAccountEditButtons from 'src/features/user/kp-user-account-page/components/UserAccountEditButtons.svelte';
    import RecordStatusService from 'src/features/record/record-status.service';
    import type {RecordStatusTransitionButtonDefinition} from 'src/features/record/types';

    export let isSutor: boolean;
    export let model: DocumentDetailView;
    export let recordHoldings: RecordHolding[] | undefined;

    export let user: User | null = null;
    export let userDetailView: UserDetailView | null = null;

    const localize = getLocalization();
    const service = getInjector().getByToken<KpDocumentDetailPageService>(KpDocumentDetailPageService.serviceName);
    const recordStatusService = getInjector().getByToken<RecordStatusService>(RecordStatusService.serviceName);

    const handleEditClick = async () => {
        await service.createRecordEditation(model.record, model.record.fond);
    }

    function getStatusTransitionButton(statusTransition: RecordStatusTransition): RecordStatusTransitionButtonDefinition {
        return recordStatusService.getStatusTransitionButton(model.record.status, statusTransition);
    }
</script>

<!-- Edit -->
{#if model.editable}
    <KpButton buttonStyle="primary"
              isBlock
              on:click={handleEditClick}>

        <IconedContent icon="edit">
            {localize(/* @kp-localization commons.editovat */ 'commons.editovat')}
        </IconedContent>
    </KpButton>
{/if}

<!-- Update record -->
{#if !isSutor && model.editable}
    <KpButton isBlock
              on:click={() => service.updateWithExternalRecord(model.record)}
              title="{localize(/* @kp-localization detail.UpdateRecordTooltip */ 'detail.UpdateRecordTooltip')}">

        <IconedContent icon="refresh">
            {localize(/* @kp-localization detail.UpdateRecord */ 'detail.UpdateRecord')}
        </IconedContent>
    </KpButton>
{/if}

<!-- Copy record -->
{#if model.creatable}
    <KpButton isBlock
              on:click={() => service.copyRecord(model.record)}>

        <IconedContent icon="copy-alt">
            {localize(/* @kp-localization commons.Zkopirovat */ 'commons.Zkopirovat')}
        </IconedContent>
    </KpButton>
{/if}

<!-- Connected files -->
{#if !isSutor && model.filesManageable}
    <KpButtonStyleAnchor isBlock
                         href="/media-viewer?rootDirectory={model.record.directoryId}&edit=true&origin={pipe(window.location.href, encodeUri())}">
        {localize(/* @kp-localization file.PripojeneSoubory */ 'file.PripojeneSoubory')}
    </KpButtonStyleAnchor>
{/if}

{#if !isSutor}
    {#each model.record.recordStatusTransitions as statusTransition}
        {@const statusTransitionButton = getStatusTransitionButton(statusTransition)}
        <KpActionRequestButton isBlock
                               path="/api/records/status"
                               requestMethod="post"
                               requestBody="{{record: model.record.id, status: statusTransition.targetStatus.id}}"
                               isDisabled="{!statusTransition.permission.allowed}"
                               on:success={() => service.refreshPage()}
                               title="{statusTransitionButton.title}">
            {statusTransitionButton.longText}
        </KpActionRequestButton>
    {/each}
{/if}

{#if model.utilityActionable}
    <KpActionRequestButton isBlock
                           path="/api/records/refresh"
                           requestMethod="post"
                           requestBody="{{record: model.record.id}}"
                           on:success={() => service.refreshPage()}>
        Regenerovat & reindexovat
    </KpActionRequestButton>
{/if}

{#if exists(recordHoldings)}
    <!-- Delete 1 holding -->
    {#if model.holdingDeletable && recordHoldings.length === 1}
        <KpButton isBlock
                  on:click={() => service.deleteRecordHolding(recordHoldings[0], recordHoldings)}>

            {localize(/* @kp-localization detail.DeleteDocument */ 'detail.DeleteDocument')} z {pipe(recordHoldings[0].department, loc())}
        </KpButton>
    {/if}

    <!-- Delete holdings dropdown -->
    {#if model.holdingDeletable && recordHoldings.length > 1}
        <KpDropdownMenuButton isBlock>
            <svelte:fragment slot="button">
                <IconedContent icon="trash" trailingIcon="angle-small-down">
                    {localize(/* @kp-localization detail.DeleteDocument */ 'detail.DeleteDocument')}
                </IconedContent>
            </svelte:fragment>

            <svelte:fragment slot="menu">
                {#each recordHoldings as recordHolding(recordHolding.id)}
                    <KpDropdownMenuItem
                            on:click={() => service.deleteRecordHolding(recordHolding, recordHoldings)}>
                        z {pipe(recordHolding.department, loc())}
                    </KpDropdownMenuItem>
                {/each}
            </svelte:fragment>
        </KpDropdownMenuButton>
    {/if}

    <!-- Discard 1 holding -->
    {#if model.holdingDiscardable && recordHoldings.length === 1}
        <KpButton isBlock
                  on:click={() => service.discardRecordHolding(recordHoldings[0])}>
            {localize(/* @kp-localization detail.DiscardDocument */ 'detail.DiscardDocument')}  z {pipe(recordHoldings[0].department, loc())}
        </KpButton>
    {/if}

    <!-- Discard holdings dropdown -->
    {#if model.holdingDiscardable && recordHoldings.length > 1}
        <KpDropdownMenuButton isBlock>
            <svelte:fragment slot="button">
                <IconedContent icon="trash" trailingIcon="angle-small-down">
                    {localize(/* @kp-localization detail.DeleteDocument */ 'detail.DiscardDocument')}
                </IconedContent>
            </svelte:fragment>

            <svelte:fragment slot="menu">
                {#each recordHoldings as recordHolding(recordHolding.id)}
                    <KpDropdownMenuItem
                            on:click={() => service.discardRecordHolding(recordHolding)}>
                        z {pipe(recordHolding.department, loc())}
                    </KpDropdownMenuItem>
                {/each}
            </svelte:fragment>
        </KpDropdownMenuButton>
    {/if}
{/if}

<!-- Assign holding -->
{#if model.holdingAssignable}
    <KpButton isBlock
              on:click={() => service.assignRecordHolding(model.record)}>

        {localize(/* @kp-localization detail.AssignRecordHolding */ 'detail.AssignRecordHolding')}
    </KpButton>
{/if}

<!-- Collection insert button -->
{#if model.intoCollectionsInsertable}
    <KpCollectionSingleInsertButton record="{model.record}"/>
{/if}

{#if exists(user) && exists(userDetailView)}
    <small class="text-muted">Uživatelské nastavení</small>
    <UserAccountEditButtons {user} detailView="{userDetailView}" fromSutorRecordDetail="{isSutor}"/>
{/if}